import { useCallback, useEffect, useRef } from "react";

/**
 * CHP-422: keep a Radix Dialog/Sheet open when an "outside" interaction is
 * really just dismissing an open nested popper (a Select dropdown, etc.).
 *
 * Radix renders Select/popper content in a portal OUTSIDE the dialog's DOM, so
 * an outside-click while a dropdown is open reaches the Sheet's dismissable
 * layer and closes the whole Sheet instead of only the dropdown. The intent is
 * to swallow that first outside-interaction so the dropdown closes alone; a
 * later interaction with no popper open closes the Sheet as normal.
 *
 * Why a capture-phase flag and not a live `document.querySelector` inside
 * `onInteractOutside`: on the production bundle the Select unmounts its popper
 * mid-dispatch — measured on the DEV VM, the popper is gone ~8ms into the
 * pointerdown, BEFORE the dialog's `onInteractOutside` runs — so a live query
 * always misses and the Sheet closes anyway. (This is why the bug is prod-only
 * and does not reproduce in Vite dev, where StrictMode's double-invoke changes
 * the timing.) Instead we record whether a popper was open on the capture-phase
 * `pointerdown` (which fires first, while the popper is still mounted) and read
 * that flag when `onInteractOutside` fires.
 *
 * Usage:
 *   const onInteractOutside = usePopperAwareInteractOutside();
 *   <SheetContent onInteractOutside={onInteractOutside}>
 */
export function usePopperAwareInteractOutside() {
  const popperOpenAtPointerDown = useRef(false);

  useEffect(() => {
    const onPointerDownCapture = () => {
      popperOpenAtPointerDown.current = !!document.querySelector(
        "[data-radix-popper-content-wrapper]",
      );
    };
    // Capture phase: runs before Radix's document-level dismiss listeners, so it
    // observes the popper while it is still mounted.
    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, []);

  return useCallback((event: { preventDefault: () => void }) => {
    if (popperOpenAtPointerDown.current) {
      event.preventDefault();
    }
  }, []);
}
